// /events/:id — public event detail.
//
// Shows the event description, venue, dates, slots and pay. If the viewer is
// signed in as an artist, surfaces "Submit interest" (or current state +
// Withdraw). If the viewer owns the event, surfaces a small "Manage" link to
// /host/events/$id/manage.

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { MarketingShell } from '../../components/marketing/MarketingShell';
import { StateBadge } from '../../components/StateBadge';
import { PageFrame } from '../../components/ui/PageFrame';
import { csrfHeaders, useAuth } from '../../lib/auth-context';
import type { AnyState } from '../../lib/display-state';
import { track } from '../../lib/telemetry';

// STABLE, module-scope full-width AppShell wrapper. Defining this inline in the
// render body (`(props) => <AppShell tone="full">…`) mints a new component
// identity on every render, so React unmounts + remounts the whole subtree each
// keystroke — dropping focus in the interest-note Textarea (FR-027). Hoisting it
// keeps the identity stable across renders.
function AppShellFull({ children }: { children: React.ReactNode }) {
  return <AppShell tone="full">{children}</AppShell>;
}

export const Route = createFileRoute('/events/$id')({
  component: EventDetailPage,
});

type EventDetail = {
  event: {
    id: string;
    title: string;
    description: string | null;
    startsAt: number;
    endsAt: number;
    slotsTotal: number;
    slotsFilled: number;
    slotsRemaining: number;
    payCents: number | null;
    payNotes: string | null;
    state: AnyState['event'];
    publishedAt: number | null;
    createdAt: number;
    updatedAt: number;
  };
  venue: { id: string; slug: string; name: string; city: string; country: string };
  host: { id: string };
  viewer: {
    isOwner: boolean;
    isAdmin: boolean;
    myInterest:
      | { id: string; state: AnyState['event_interest'] }
      | null;
  };
};

function EventDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [data, setData] = useState<EventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');
  const [opened, { open, close }] = useDisclosure(false);

  // Track event.detail_viewed exactly once per successful load (per event id).
  const viewedRef = useRef<string | null>(null);
  // Track when the interest modal opened so we can emit timeOpenMs on close.
  const modalOpenedAtRef = useRef<number | null>(null);
  const modalSubmittedRef = useRef<boolean>(false);

  const refresh = async () => {
    try {
      const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      const json = (await res.json()) as
        | ({ ok: true } & EventDetail)
        | { ok: false; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        const msg =
          'error' in json && json.error?.message
            ? json.error.message
            : "Couldn't load this event.";
        setError(msg);
        return;
      }
      setData(json);
      setError(null);
    } catch {
      setError('Network problem loading the event.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fire event.detail_viewed when the event payload first arrives.
  useEffect(() => {
    if (data === null) return;
    if (viewedRef.current === data.event.id) return;
    viewedRef.current = data.event.id;
    track('event.detail_viewed', {
      state: data.event.state,
      myInterest: data.viewer.myInterest?.state ?? null,
      slotsRemaining: data.event.slotsRemaining,
      isOwner: data.viewer.isOwner,
    });
  }, [data]);

  function openInterestModal(): void {
    modalOpenedAtRef.current = Date.now();
    modalSubmittedRef.current = false;
    track('event.interest_modal_opened', { eventId: id });
    open();
  }

  function closeInterestModal(): void {
    const openedAt = modalOpenedAtRef.current;
    track('event.interest_modal_closed', {
      eventId: id,
      submitted: modalSubmittedRef.current,
      draftChars: interestMessage.length,
      timeOpenMs: openedAt !== null ? Date.now() - openedAt : 0,
    });
    modalOpenedAtRef.current = null;
    close();
  }

  async function submitInterest(): Promise<void> {
    setSubmitting(true);
    setActionError(null);
    try {
      const body: Record<string, unknown> = {};
      const trimmed = interestMessage.trim();
      if (trimmed.length > 0) body['message'] = trimmed;
      const res = await fetch(`/api/events/${encodeURIComponent(id)}/interests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't submit interest.");
        return;
      }
      modalSubmittedRef.current = true;
      track('event.interest_submitted', {
        eventId: id,
        messageChars: trimmed.length,
      });
      closeInterestModal();
      setInterestMessage('');
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function withdrawInterest(interestId: string): Promise<void> {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(
        `/api/events/interests/${encodeURIComponent(interestId)}/withdraw`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({}),
        },
      );
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't withdraw.");
        return;
      }
      track('event.interest_withdrawn', { eventId: id });
      await refresh();
    } catch {
      setActionError('Network problem. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Dual-shell: anonymous viewers get the marketing chrome; signed-in viewers
  // get the AppShell. AppShell uses tone="full" here so PageFrame inside the
  // body lays out content identically across both shells (MarketingShell has
  // no opinion on width).
  const Shell = user === null ? MarketingShell : AppShellFull;

  if (error !== null) {
    return (
      <Shell>
        <PageFrame tone="content">
          <Alert color="red">{error}</Alert>
          <Group mt="md">
            <Anchor component={Link} to="/events">
              Back to events
            </Anchor>
          </Group>
        </PageFrame>
      </Shell>
    );
  }
  if (data === null) {
    return (
      <Shell>
        <PageFrame tone="content">
          <Group justify="center">
            <Loader />
          </Group>
        </PageFrame>
      </Shell>
    );
  }

  const { event, venue, viewer } = data;
  const canSubmit =
    user !== null &&
    !viewer.isOwner &&
    event.state === 'published' &&
    (viewer.myInterest === null ||
      viewer.myInterest.state === 'withdrawn' ||
      viewer.myInterest.state === 'declined');

  return (
    <Shell>
      <PageFrame tone="content">
          <Stack gap="lg">
            <Group justify="space-between" wrap="wrap" align="flex-start">
              <Stack gap={4}>
                <Text size="sm" c="dimmed" tt="uppercase">
                  Event at {venue.name}
                </Text>
                <Title order={1}>{event.title}</Title>
                <Text c="dimmed">
                  {venue.city}, {venue.country.toUpperCase()}
                </Text>
              </Stack>
              <Stack gap="xs" align="flex-end">
                <StateBadge entity="event" state={event.state} size="lg" />
                {viewer.isOwner ? (
                  <Link
                    to="/host/events/$id/manage"
                    params={{ id: event.id }}
                    style={{ textDecoration: 'underline', fontSize: 14 }}
                  >
                    Manage event →
                  </Link>
                ) : null}
              </Stack>
            </Group>

            <Card withBorder padding="lg" radius="md">
              <Stack gap="sm">
                <Group gap="md">
                  <Badge color="gray" variant="light" size="lg">
                    {formatRange(event.startsAt, event.endsAt)}
                  </Badge>
                  <Badge color="gray" variant="light" size="lg">
                    {event.slotsRemaining} of {event.slotsTotal} slots open
                  </Badge>
                </Group>
                <Text size="sm">{formatPay(event.payCents, event.payNotes)}</Text>
              </Stack>
            </Card>

            {event.description !== null && event.description.length > 0 ? (
              <Card withBorder padding="lg" radius="md">
                <Stack gap="xs">
                  <Title order={5}>About this event</Title>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{event.description}</Text>
                </Stack>
              </Card>
            ) : null}

            <Card withBorder padding="lg" radius="md">
              <Stack gap="xs">
                <Title order={5}>Venue</Title>
                <Text fw={600}>{venue.name}</Text>
                <Text size="sm" c="dimmed">
                  {venue.city}, {venue.country.toUpperCase()}
                </Text>
                <Link
                  to="/venues/$slug"
                  params={{ slug: venue.slug }}
                  style={{ textDecoration: 'underline', fontSize: 14 }}
                >
                  View venue
                </Link>
              </Stack>
            </Card>

            {actionError !== null ? <Alert color="red">{actionError}</Alert> : null}

            {/* Viewer-side action panel */}
            {user === null ? (
              <Card withBorder padding="lg" radius="md">
                <Stack gap="sm">
                  <Text>Sign in to send interest to this host.</Text>
                  <Group>
                    <Button component={Link} to="/sign-in">
                      Sign in
                    </Button>
                    <Anchor component={Link} to="/sign-up">
                      Or create an account
                    </Anchor>
                  </Group>
                </Stack>
              </Card>
            ) : viewer.isOwner ? null : viewer.myInterest !== null &&
              viewer.myInterest.state === 'submitted' ? (
              <Card withBorder padding="lg" radius="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Stack gap={2}>
                      <Text fw={600}>Interest sent</Text>
                      <Text size="sm" c="dimmed">
                        The host will review it and either invite you in or pass.
                      </Text>
                    </Stack>
                    <StateBadge
                      entity="event_interest"
                      state={viewer.myInterest.state}
                    />
                  </Group>
                  <Group>
                    <Button
                      variant="subtle"
                      color="gray"
                      loading={submitting}
                      onClick={() => {
                        if (viewer.myInterest !== null) {
                          void withdrawInterest(viewer.myInterest.id);
                        }
                      }}
                    >
                      Withdraw interest
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ) : viewer.myInterest !== null && viewer.myInterest.state === 'accepted' ? (
              <Alert color="tide" variant="light">
                Accepted. The exchange has been opened. Find it under your exchanges.
              </Alert>
            ) : canSubmit ? (
              <Card withBorder padding="lg" radius="md">
                <Stack gap="sm">
                  <Stack gap={4}>
                    <Text fw={600}>Interested in this slot?</Text>
                    <Text size="sm" c="dimmed">
                      Send a short note. The host will reply if it's a fit.
                    </Text>
                  </Stack>
                  <Group>
                    <Button
                      onClick={openInterestModal}
                      variant="gradient"
                      gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                    >
                      Submit interest
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ) : event.state !== 'published' ? (
              <Alert color="gray" variant="light">
                This event isn't open for new interest right now.
              </Alert>
            ) : null}
          </Stack>
      </PageFrame>

      <Modal opened={opened} onClose={closeInterestModal} title="Send a note" size="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Hosts read this alongside your profile. Skip it if you'd rather let your work
            speak first.
          </Text>
          <Textarea
            label="Anything you'd like to say up front? (optional)"
            autosize
            minRows={3}
            maxRows={6}
            placeholder="What you'd bring to a room like this in two sentences."
            value={interestMessage}
            onChange={(e) => setInterestMessage(e.currentTarget.value)}
            maxLength={200}
          />
          <Text size="xs" c="dimmed" ta="right">
            {interestMessage.length} / 200
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeInterestModal}>
              Cancel
            </Button>
            <Button
              loading={submitting}
              onClick={() => {
                void submitInterest();
              }}
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Send interest
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Shell>
  );
}

function formatRange(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
  }
  return `${dateFmt.format(start)} → ${dateFmt.format(end)}`;
}

function formatPay(cents: number | null, notes: string | null): string {
  if (cents !== null && cents > 0) {
    const dollars = (cents / 100).toFixed(0);
    return notes !== null ? `$${dollars} AUD · ${notes}` : `$${dollars} AUD`;
  }
  return notes ?? 'Pay as discussed with the host.';
}
